import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import moment from "moment";
import FlatPickr from "react-flatpickr";
import PulseLoader from "react-spinners/PulseLoader";
import { toast, ToastContainer } from "react-toastify";
import {
  BarChart3,
  Calendar,
  Tag,
  MapPin,
  Mail,
  Search,
  Download,
  FileSpreadsheet,
  DollarSign,
  Ticket,
  Percent,
  X,
  Filter,
  CheckCircle2,
  ShieldCheck,
  AlertCircle,
  FileText,
  Clock,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { calculateSummaryStats } from "../../../utils/revenueCalculator";
import {
  getAllBookingByDateRange,
  getAllEventByOrganizer,
  getAllTicketBySlug,
  getAllVenueBySlug,
  getReport,
  searchBookingsByContact,
  downloadTicketByBookingNo,
} from "../../../api/index";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import Background from "../../Login/Background";
import { useNightMode } from "../../../contexts/NightModeContext";

interface Booking {
  bookingId: string;
  organizerId: string;
  [key: string]: any;
}

const searchTypeOptions = [
  { value: "date", label: "Date Range", icon: Calendar },
  { value: "event", label: "Event", icon: FileText },
  { value: "ticket", label: "Ticket Type", icon: Tag },
  { value: "venue", label: "Venue", icon: MapPin },
  { value: "email", label: "Email / Phone", icon: Mail },
];

const RunReport: React.FC = () => {
  const { isNightMode } = useNightMode();

  const organizerData = localStorage.getItem("organizerData");
  const organizerId = organizerData ? JSON.parse(organizerData)?.organizerId : null;

  const [searchCriteria, setSearchCriteria] = useState({
    fromDate: null as Date | null,
    toDate: null as Date | null,
    selectedEvent: null as any,
    selectedTicketType: null as any,
    selectedVenue: null as any,
    emailOrPhone: "",
    searchType: "date",
  });

  const [eventList, setEventList] = useState<any[]>([]);
  const [venueList, setVenueList] = useState<any[]>([]);
  const [ticketTypeList, setTicketTypeList] = useState<any[]>([]);
  const [bookingList, setBookingList] = useState<Booking[]>([]);
  const [filteredBookingList, setFilteredBookingList] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadingTicketId, setDownloadingTicketId] = useState<string | null>(null);
  const [tableSearchQuery, setTableSearchQuery] = useState("");

  const [summaryStats, setSummaryStats] = useState<any>({});

  useEffect(() => {
    async function fetchInitialData() {
      if (!organizerId) return;
      setLoading(true);
      try {
        const eventsResp: any[] = await getAllEventByOrganizer();
        if (Array.isArray(eventsResp)) {
          const eventOptions = eventsResp.map((item: any) => ({
            value: item.eventId,
            label: item.eventName,
            slugId: item.slugId,
          }));
          setEventList(eventOptions);
        }
      } catch (err) {
        console.error("Failed to load organizer events:", err);
        setError("Failed to load events.");
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, [organizerId]);

  const handleEventChange = async (selectedOption: any) => {
    setSearchCriteria((prev) => ({ ...prev, selectedEvent: selectedOption, selectedTicketType: null, selectedVenue: null }));
    if (selectedOption?.slugId) {
      try {
        const [venuesResp, ticketsResp] = await Promise.all([
          getAllVenueBySlug(selectedOption.slugId),
          getAllTicketBySlug(selectedOption.slugId),
        ]);
        if (Array.isArray(venuesResp)) {
          setVenueList(venuesResp.map((item: any) => ({ value: item.venueId, label: item.venueName })));
        }
        if (Array.isArray(ticketsResp)) {
          setTicketTypeList(ticketsResp.map((item: any) => ({ value: item.ticketTypeId, label: item.ticketType, price: item.price })));
        }
      } catch (err) {
        console.error("Error loading event venues & ticket types:", err);
      }
    } else {
      setVenueList([]);
      setTicketTypeList([]);
    }
  };

  const performBookingSearch = async () => {
    if (!organizerId) return;
    setLoading(true);
    setError("");
    let bookings: Booking[] = [];
    const ensureArray = (res: any): Booking[] => {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.data)) return res.data;
      if (res && Array.isArray(res.bookings)) return res.bookings;
      return [];
    };

    try {
      switch (searchCriteria.searchType) {
        case "date": {
          if (searchCriteria.fromDate && searchCriteria.toDate) {
            const resp = await getAllBookingByDateRange(searchCriteria.fromDate, searchCriteria.toDate);
            bookings = ensureArray(resp);
          }
          break;
        }
        case "event": {
          if (searchCriteria.selectedEvent) {
            const resp = await getReport(searchCriteria.selectedEvent.value);
            bookings = ensureArray(resp);
          }
          break;
        }
        case "ticket": {
          if (searchCriteria.selectedEvent && searchCriteria.selectedTicketType) {
            const resp = await getReport(searchCriteria.selectedEvent.value);
            const arr = ensureArray(resp);
            bookings = arr.filter((b: Booking) => Number(b.ticketTypeId) === Number(searchCriteria.selectedTicketType.value));
          }
          break;
        }
        case "venue": {
          if (searchCriteria.selectedEvent && searchCriteria.selectedVenue) {
            const resp = await getReport(searchCriteria.selectedEvent.value);
            const arr = ensureArray(resp);
            bookings = arr.filter((b: Booking) => Number(b.venueId) === Number(searchCriteria.selectedVenue.value));
          }
          break;
        }
        case "email": {
          if (searchCriteria.emailOrPhone.trim()) {
            const resp = await searchBookingsByContact(searchCriteria.emailOrPhone);
            bookings = ensureArray(resp?.data || resp);
          }
          break;
        }
        default:
          break;
      }
      const filteredByOrganizer = bookings.filter((b: Booking) => Number(b.organizerId) === Number(organizerId));
      const activeBookings = filteredByOrganizer.filter((b: Booking) => b.paymentStatus !== "expired");
      setBookingList(activeBookings);
      setFilteredBookingList(activeBookings);
      setSummaryStats(calculateSummaryStats(activeBookings));
    } catch (err) {
      console.error(err);
      setBookingList([]);
      setFilteredBookingList([]);
      setSummaryStats(calculateSummaryStats([]));
    } finally {
      setLoading(false);
    }
  };

  const isSearchValid = () => {
    switch (searchCriteria.searchType) {
      case "date":
        return Boolean(searchCriteria.fromDate && searchCriteria.toDate);
      case "event":
        return Boolean(searchCriteria.selectedEvent);
      case "ticket":
        return Boolean(searchCriteria.selectedEvent && searchCriteria.selectedTicketType);
      case "venue":
        return Boolean(searchCriteria.selectedEvent && searchCriteria.selectedVenue);
      case "email":
        return searchCriteria.emailOrPhone.trim().length > 0;
      default:
        return false;
    }
  };

  const handleClear = () => {
    setSearchCriteria({
      fromDate: null,
      toDate: null,
      selectedEvent: null,
      selectedTicketType: null,
      selectedVenue: null,
      emailOrPhone: "",
      searchType: "date",
    });
    setFilteredBookingList([]);
    setBookingList([]);
    setError("");
  };

  const handleDownloadTicket = async (row: any) => {
    const bookingNo = row?.bookingNo || row?.originalBooking?.primaryBookingNo || row?.originalBooking?.bookingNo;
    if (!bookingNo) return;
    try {
      setDownloadingTicketId(bookingNo);
      const response = await downloadTicketByBookingNo(bookingNo);
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        toast.error("Failed to download ticket PDF.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to download ticket PDF.");
    } finally {
      setDownloadingTicketId(null);
    }
  };

  const transformedData = useMemo(() => {
    return filteredBookingList.map((booking) => {
      const contactDetails = booking.bookingObject?.primaryContactDetails || {};
      return {
        id: booking.bookingId || booking.bookingNo,
        bookingId: booking.bookingId,
        bookingNo: booking.bookingNo || "N/A",
        name: `${contactDetails.firstName || ""} ${contactDetails.lastName || ""}`.trim() || "Guest",
        email: contactDetails.email || "N/A",
        phone: contactDetails.phoneNo || "N/A",
        ticketType: booking.TicketType?.ticketType || booking.ticketType || "Standard Pass",
        ticketCount: booking.totalTicketCount || 1,
        ticketAmount: Number(booking.totalTicketAmount || 0),
        grandTotal: Number(booking.grandTotal || 0),
        paymentStatus: booking.paymentStatus || "pending",
        bookingStatus: booking.bookingStatus || "confirmed",
        eventName: booking.Event?.eventName || "N/A",
        venueName: booking.Venue?.venueName || "N/A",
        createdAt: booking.createdAt,
        originalBooking: booking,
      };
    });
  }, [filteredBookingList]);

  const exportToExcel = () => {
    if (!transformedData.length) return;
    try {
      const XLSX = require("xlsx");
      const FileSaver = require("file-saver");

      const cleanData = transformedData.map((row) => ({
        "Booking No": row.bookingNo,
        "Customer Name": row.name,
        "Email Address": row.email,
        "Phone Number": row.phone,
        "Ticket Type": row.ticketType,
        "Ticket Quantity": row.ticketCount,
        "Ticket Amount": `$${row.ticketAmount.toFixed(2)}`,
        "Grand Total": `$${row.grandTotal.toFixed(2)}`,
        "Payment Status": row.paymentStatus,
        "Booking Status": row.bookingStatus,
        "Event Title": row.eventName,
        "Venue Name": row.venueName,
        "Created Date": row.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm") : "N/A",
      }));

      const worksheet = XLSX.utils.json_to_sheet(cleanData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Booking Report");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      FileSaver.saveAs(file, `booking_report_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
      toast.success("Report spreadsheet exported!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel report.");
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "bookingNo",
        header: "BOOKING NO",
        cell: ({ row }) => (
          <span className="font-mono text-white fw-bold fs-12px">{row.original.bookingNo}</span>
        ),
      },
      {
        accessorKey: "name",
        header: "NAME",
        cell: ({ row }) => (
          <span className="fw-bold contact-name-text fs-13px">{row.original.name}</span>
        ),
      },
      {
        accessorKey: "email",
        header: "EMAIL",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original.email}</span>
        ),
      },
      {
        accessorKey: "phone",
        header: "PHONE",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original.phone}</span>
        ),
      },
      {
        accessorKey: "ticketType",
        header: "TICKET TYPE",
        cell: ({ row }) => (
          <span
            style={{
              padding: "3px 10px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 600,
              background: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              fontFamily: "monospace",
              whiteSpace: "nowrap",
            }}
          >
            {row.original.ticketType}
          </span>
        ),
      },
      {
        accessorKey: "ticketCount",
        header: "TICKET COUNT",
        cell: ({ row }) => (
          <span className="font-mono text-white fw-semibold fs-12px">{row.original.ticketCount}</span>
        ),
      },
      {
        accessorKey: "ticketAmount",
        header: "TICKET AMOUNT",
        cell: ({ row }) => (
          <span className="font-mono text-white fw-semibold fs-12px">
            ${row.original.ticketAmount.toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "grandTotal",
        header: "GRAND TOTAL",
        cell: ({ row }) => (
          <span className="font-mono text-pink fw-bold fs-13px">
            ${row.original.grandTotal.toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "paymentStatus",
        header: "PAYMENT STATUS",
        cell: ({ row }) => {
          const status = String(row.original.paymentStatus).toLowerCase();
          const isPaid = status === "paid";
          return (
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                background: isPaid ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
                color: isPaid ? "#34D399" : "#FBBF24",
                border: isPaid ? "1px solid rgba(16, 185, 129, 0.35)" : "1px solid rgba(245, 158, 11, 0.35)",
                boxShadow: isPaid ? "0 2px 10px rgba(16, 185, 129, 0.2)" : "0 2px 10px rgba(245, 158, 11, 0.2)",
                whiteSpace: "nowrap",
              }}
            >
              {isPaid ? <CheckCircle2 size={12} color="#34D399" /> : <Clock size={12} color="#FBBF24" />}
              {isPaid ? "Paid" : status.toUpperCase()}
            </span>
          );
        },
      },
      {
        accessorKey: "bookingStatus",
        header: "BOOKING STATUS",
        cell: ({ row }) => {
          const status = String(row.original.bookingStatus).toLowerCase();
          const isConfirmed = status === "confirmed";
          return (
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                background: isConfirmed ? "rgba(238, 63, 124, 0.15)" : "rgba(148, 163, 184, 0.15)",
                color: isConfirmed ? "#EE3F7C" : "#94A3B8",
                border: isConfirmed ? "1px solid rgba(238, 63, 124, 0.35)" : "1px solid rgba(148, 163, 184, 0.3)",
                boxShadow: isConfirmed ? "0 2px 10px rgba(238, 63, 124, 0.2)" : "none",
                whiteSpace: "nowrap",
              }}
            >
              <ShieldCheck size={12} color={isConfirmed ? "#EE3F7C" : "#94A3B8"} />
              {isConfirmed ? "Confirmed" : status.toUpperCase()}
            </span>
          );
        },
      },
      {
        accessorKey: "eventName",
        header: "EVENT NAME",
        cell: ({ row }) => (
          <span className="contact-name-text fs-12px fw-semibold">{row.original.eventName}</span>
        ),
      },
      {
        accessorKey: "venueName",
        header: "VENUE NAME",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original.venueName}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "CREATED DATE",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-11px font-mono">
            {row.original.createdAt ? <FormattedDate dateString={row.original.createdAt} /> : "N/A"}
          </span>
        ),
      },
      {
        id: "action",
        header: "ACTION",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const isDownloading = downloadingTicketId === row.original.bookingNo;
          return (
            <button
              type="button"
              disabled={isDownloading}
              onClick={() => handleDownloadTicket(row.original)}
              style={{
                padding: "6px 14px",
                borderRadius: "8px",
                fontSize: "11px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)",
                color: "#ffffff",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(238, 63, 124, 0.3)",
                transition: "transform 0.15s ease, boxShadow 0.15s ease",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(238, 63, 124, 0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(238, 63, 124, 0.3)";
              }}
            >
              {isDownloading ? (
                <PulseLoader color="#ffffff" size={4} />
              ) : (
                <>
                  <Download size={13} /> Ticket
                </>
              )}
            </button>
          );
        },
      },
    ],
    [downloadingTicketId]
  );

  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: isNightMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      borderColor: state.isFocused ? "#EE3F7C" : isNightMode ? "rgba(255, 255, 255, 0.08)" : "#cbd5e1",
      borderRadius: "12px",
      padding: "2px 4px",
      minHeight: "44px",
      boxShadow: "none",
      color: isNightMode ? "#ffffff" : "#0f172a",
      "&:hover": { borderColor: "rgba(238, 63, 124, 0.4)" },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? "rgba(238, 63, 124, 0.2)" : state.isFocused ? (isNightMode ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9") : "transparent",
      color: isNightMode ? "#ffffff" : "#0f172a",
      cursor: "pointer",
      padding: "10px 14px",
      fontSize: "12px",
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: isNightMode ? "rgba(18, 18, 28, 0.98)" : "#ffffff",
      backdropFilter: "blur(24px)",
      border: isNightMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e2e8f0",
      borderRadius: "14px",
      zIndex: 99999,
      padding: "6px",
    }),
    singleValue: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a", fontSize: "12px" }),
    placeholder: (provided: any) => ({ ...provided, color: isNightMode ? "rgba(255, 255, 255, 0.35)" : "#94a3b8", fontSize: "12px" }),
  };

  return (
    <div className={`v3-wrap px-3 py-4 ${isNightMode ? "" : "light-theme"}`} style={{ position: "relative" }}>
      <Background />
      <ToastContainer position="top-right" autoClose={3000} theme={isNightMode ? "dark" : "light"} />

      {/* Header Section */}
      <PageHeader
        title="Booking Reports & Analytics"
        subtitle="Generate detailed booking reports, filter across dates, venues, or ticket tiers, and export records."
        icon={BarChart3}
        isLoading={loading}
        actions={
          transformedData.length > 0 ? (
            <button type="button" className="v3-btn-primary" onClick={exportToExcel} style={{ fontSize: 12 }}>
              <FileSpreadsheet size={15} /> Export Report (.XLSX)
            </button>
          ) : undefined
        }
      />

      {/* Filter Card */}
      <div className="glass-panel mb-4" style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative", zIndex: 10 }}>
        <div className="v3-section-head">
          <Filter size={14} color="#EE3F7C" /> Search Criteria & Filters
        </div>

        {/* Search Type Tabs */}
        <div>
          <label className="v3-label">Search Criteria Mode</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {searchTypeOptions.map((opt) => {
              const Icon = opt.icon;
              const isActive = searchCriteria.searchType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSearchCriteria({ ...searchCriteria, searchType: opt.value, selectedEvent: null, selectedTicketType: null, selectedVenue: null, emailOrPhone: "" })}
                  style={{
                    padding: "8px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: isActive ? "linear-gradient(135deg, #EE3F7C, #F79B59)" : "rgba(255,255,255,0.05)",
                    border: isActive ? "none" : "1px solid rgba(255,255,255,0.06)",
                    color: isActive ? "#ffffff" : "rgba(255,255,255,0.6)",
                    boxShadow: isActive ? "0 4px 14px rgba(238,63,124,0.35)" : "none",
                  }}
                >
                  <Icon size={14} /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dynamic Filter Controls */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {searchCriteria.searchType === "date" && (
            <>
              <div>
                <label className="v3-label">From Date</label>
                <FlatPickr
                  className="v3-input"
                  placeholder="Select start date"
                  value={searchCriteria.fromDate || ""}
                  onChange={(d: any) => setSearchCriteria((prev) => ({ ...prev, fromDate: d[0] || null }))}
                />
              </div>
              <div>
                <label className="v3-label">To Date</label>
                <FlatPickr
                  className="v3-input"
                  placeholder="Select end date"
                  value={searchCriteria.toDate || ""}
                  onChange={(d: any) => setSearchCriteria((prev) => ({ ...prev, toDate: d[0] || null }))}
                />
              </div>
            </>
          )}

          {(searchCriteria.searchType === "event" || searchCriteria.searchType === "ticket" || searchCriteria.searchType === "venue") && (
            <div>
              <label className="v3-label">Select Event</label>
              <Select
                value={searchCriteria.selectedEvent}
                onChange={handleEventChange}
                options={eventList}
                placeholder="Choose an event..."
                isClearable
                styles={selectStyles}
              />
            </div>
          )}

          {searchCriteria.searchType === "ticket" && (
            <div>
              <label className="v3-label">Ticket Type</label>
              <Select
                value={searchCriteria.selectedTicketType}
                onChange={(opt: any) => setSearchCriteria((prev) => ({ ...prev, selectedTicketType: opt }))}
                options={ticketTypeList}
                placeholder="Choose ticket type..."
                isClearable
                isDisabled={!searchCriteria.selectedEvent}
                styles={selectStyles}
              />
            </div>
          )}

          {searchCriteria.searchType === "venue" && (
            <div>
              <label className="v3-label">Venue</label>
              <Select
                value={searchCriteria.selectedVenue}
                onChange={(opt: any) => setSearchCriteria((prev) => ({ ...prev, selectedVenue: opt }))}
                options={venueList}
                placeholder="Choose venue..."
                isClearable
                isDisabled={!searchCriteria.selectedEvent}
                styles={selectStyles}
              />
            </div>
          )}

          {searchCriteria.searchType === "email" && (
            <div>
              <label className="v3-label">Email Address or Phone Number</label>
              <input
                type="text"
                className="v3-input"
                value={searchCriteria.emailOrPhone}
                onChange={(e) => setSearchCriteria((prev) => ({ ...prev, emailOrPhone: e.target.value }))}
                placeholder="Enter email or phone..."
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
          <button
            type="button"
            className="v3-btn-primary"
            onClick={performBookingSearch}
            disabled={loading || !isSearchValid()}
            style={{ padding: "10px 24px", fontSize: 12 }}
          >
            {loading ? <PulseLoader color="#fff" size={6} /> : <><Search size={14} /> Search Reports</>}
          </button>

          <button
            type="button"
            className="v3-btn-secondary"
            onClick={handleClear}
            style={{ padding: "10px 18px", fontSize: 12 }}
          >
            <X size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Summary Stat Cards - Rendered Below Search Criteria */}
      {transformedData.length > 0 && (
        <div className="contact-stats-grid mb-4" style={{ position: "relative", zIndex: 10 }}>
          <div className="contact-stat-card">
            <div>
              <div className="contact-stat-label">TOTAL BOOKINGS</div>
              <div className="contact-stat-value">{summaryStats?.totalBookings || 0}</div>
            </div>
            <div className="contact-stat-icon pink"><Calendar size={22} /></div>
          </div>

          <div className="contact-stat-card">
            <div>
              <div className="contact-stat-label">TOTAL REVENUE</div>
              <div className="contact-stat-value">${(summaryStats?.totalRevenue || 0).toFixed(2)}</div>
            </div>
            <div className="contact-stat-icon green"><DollarSign size={22} /></div>
          </div>

          <div className="contact-stat-card">
            <div>
              <div className="contact-stat-label">TICKETS ISSUED</div>
              <div className="contact-stat-value">{summaryStats?.totalTickets || 0}</div>
            </div>
            <div className="contact-stat-icon orange"><Ticket size={22} /></div>
          </div>

          <div className="contact-stat-card">
            <div>
              <div className="contact-stat-label">TOTAL DISCOUNTS</div>
              <div className="contact-stat-value">${(summaryStats?.totalDiscount || 0).toFixed(2)}</div>
            </div>
            <div className="contact-stat-icon purple"><Percent size={22} /></div>
          </div>
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: "rgba(255,93,115,0.1)", border: "1px solid rgba(255,93,115,0.25)", color: "#FF5D73", fontSize: 12, display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Global Quick Search Bar */}
      {transformedData.length > 0 && (
        <div className="contact-filter-card mb-3" style={{ position: "relative", zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
            <div className="position-relative flex-grow-1 w-100">
              <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
              <input
                type="text"
                className="contact-search-input"
                placeholder="Quick global filter across all fields..."
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Report Table Card via TablesV2 with enableColumnFilters & 14 explicit columns */}
      {transformedData.length > 0 && (
        <div style={{ position: "relative", zIndex: 10 }}>
          <TablesV2
            data={transformedData}
            columns={columns}
            isLoading={loading}
            enableColumnFilters={true}
            searchQuery={tableSearchQuery}
            loadingMessage="Querying Booking Registry..."
            emptyMessage="No booking records match your table filter."
          />
        </div>
      )}
    </div>
  );
};

export default RunReport;
